<template>
  <app-page title="App Analytics" icon="mobile-screen-button" class="analytics-app">
    <template #header>
      <div class="d-flex align-items-center">
        <divisions-dropdown />
        <b-button
          v-if="portalUpdatesJan2026Enabled"
          variant="primary"
          class="ml-3"
          title="Download PDF"
          @click="showModal = true"
        >
          <font-awesome-icon :icon="['fad', 'download']" />
        </b-button>
      </div>
    </template>
    <template #custom-content>
      <b-col>
        <analytics-nav />
        <snapshot-metrics />
        <track-metrics ref="trackRef" />

        <!-- Build (Workplace Savings) -->
        <build-metrics v-if="isBuildEnabled" ref="buildRef" />
        <locked-metric-panel v-else-if="portalUpdatesJan2026Enabled" product-key="build" />

        <stream-metrics ref="streamRef" />

        <!-- Budget -->
        <budget-metrics v-if="isBudgetEnabled && portalUpdatesJan2026Enabled" />
        <locked-metric-panel v-else-if="portalUpdatesJan2026Enabled" product-key="budget" />

        <!-- Rewards -->
        <rewards-metrics v-if="isRewardsEnabled && portalUpdatesJan2026Enabled" />
        <locked-metric-panel v-else-if="portalUpdatesJan2026Enabled" product-key="rewards" />

        <!-- SDL -->
        <sdl-metrics v-if="isSdlEnabled" ref="sdlRef" />
        <locked-metric-panel v-else-if="portalUpdatesJan2026Enabled" product-key="sdl" />

        <!-- Coach -->
        <coach-metrics v-if="isCoachEnabled && portalUpdatesJan2026Enabled" />
        <locked-metric-panel v-else-if="portalUpdatesJan2026Enabled" product-key="coach" />

        <coach-metrics-stub v-if="tescoPortalStubsEnabled" />
      </b-col>
      <subsection-download-modal :sections="downloadSections" :visible="showModal" @hidden="showModal = false" />
    </template>
  </app-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import AppPage from '@/components/AppPage.vue';
import SubsectionDownloadModal from '@/components/SubsectionDownloadModal.vue';
import BudgetMetrics from '@/pages/analyticsApp/components/BudgetMetrics.vue';
import BuildMetrics from '@/pages/analyticsApp/components/BuildMetrics.vue';
import CoachMetrics from '@/pages/analyticsApp/components/CoachMetrics.vue';
import CoachMetricsStub from '@/pages/analyticsApp/components/CoachMetricsStub.vue';
import LockedMetricPanel from '@/pages/analyticsApp/components/LockedMetricPanel.vue';
import RewardsMetrics from '@/pages/analyticsApp/components/RewardsMetrics.vue';
import SnapshotMetrics from '@/pages/analyticsApp/components/SnapshotMetrics.vue';
import StreamMetrics from '@/pages/analyticsApp/components/StreamMetrics.vue';
import TrackMetrics from '@/pages/analyticsApp/components/TrackMetrics.vue';
import AnalyticsNav from '@/pages/analyticsHomepage/components/AnalyticsNav.vue';
import DivisionsDropdown from '@/pages/analyticsHomepage/components/DivisionsDropdown.vue';
import SdlMetrics from '@/pages/analyticsApp/components/SdlMetrics.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useProductAvailability from '@/composables/useProductAvailability';

const { portalUpdatesJan2026Enabled, tescoPortalStubsEnabled } = useFeatureFlags();
const { isCoachEnabled, isBudgetEnabled, isRewardsEnabled, isSdlEnabled, isBuildEnabled } = useProductAvailability();

const showModal = ref(false);
const trackRef = ref(null);
const buildRef = ref(null);
const streamRef = ref(null);
const sdlRef = ref(null);

// Download-eligible sections must:
//   1. Self-hide via `<section v-if="loading || hasData">`.
//   2. `defineExpose({ hasData })` where hasData = !loading && data is non-empty.
//   3. Be registered here.
const sectionSpecs = [
  { key: 'track', label: 'Track', ref: trackRef },
  { key: 'savings', label: 'Savings', ref: buildRef },
  { key: 'pay', label: 'Pay', ref: streamRef },
  { key: 'loans', label: 'Loans', ref: sdlRef },
];

const downloadSections = computed(() =>
  sectionSpecs
    .filter(spec => spec.ref.value?.hasData)
    .map(spec => ({
      key: spec.key,
      label: spec.label,
      inlineKpis: true,
      getContainerEl: () => spec.ref.value?.$el,
    })),
);
</script>
